import { useState, useEffect, useMemo } from "react";
import { z } from "zod";
import { Loader2, CheckCircle, Check, X } from "lucide-react";
import { supabase, getTenantHeaders } from "@/integrations/supabase/tenantClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/common/Logo";

// Password strength calculation
const getPasswordStrength = (pwd: string) => {
  const checks = {
    minLength: pwd.length >= 8,
    maxLength: pwd.length <= 72,
    hasUppercase: /[A-Z]/.test(pwd),
    hasLowercase: /[a-z]/.test(pwd),
    hasNumber: /[0-9]/.test(pwd),
  };

  const requiredChecks = [checks.minLength, checks.hasUppercase, checks.hasLowercase, checks.hasNumber];
  const passedCount = requiredChecks.filter(Boolean).length;
  const strength = passedCount <= 1 ? 'weak' : passedCount <= 3 ? 'medium' : 'strong';

  return { checks, strength, passedCount };
};

// Requirement item component
const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
  <div className="flex items-center gap-2">
    {met ? (
      <Check className="h-3.5 w-3.5 text-green-500" />
    ) : (
      <X className="h-3.5 w-3.5 text-gray-500" />
    )}
    <span className={met ? "text-green-500" : "text-gray-500"}>{text}</span>
  </div>
);

const passwordSchema = z.string()
  .min(8, "Password must be at least 8 characters")
  .max(72, "Password must be less than 72 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number");

interface ResetPasswordFormProps {
  onSuccess: () => void;
  onBack: () => void;
  email?: string;
  token?: string;
}

export function ResetPasswordForm({ onSuccess, onBack, email, token }: ResetPasswordFormProps) {
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);

  // Calculate password strength in real-time
  const passwordStrength = useMemo(() => getPasswordStrength(password), [password]);

  // Countdown effect for auto-redirect after success
  useEffect(() => {
    if (!resetSuccess) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onSuccess();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resetSuccess, onSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setConfirmError(null);

    // Validate password
    const result = passwordSchema.safeParse(password);
    if (!result.success) {
      setPasswordError(result.error.errors[0].message);
      return;
    }

    // Check passwords match
    if (password !== confirmPassword) {
      setConfirmError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      // If we have a token, use the edge function to reset password
      if (token && email) {
        const { data, error: resetError } = await supabase.functions.invoke('reset-password', {
          body: { token, email, password },
          headers: getTenantHeaders(),
        });

        if (resetError || data?.error) {
          throw new Error(data?.error || resetError?.message || "Failed to reset password");
        }
      } else {
        // No token provided - this shouldn't happen with our custom flow
        throw new Error("Invalid reset session. Please request a new password reset link.");
      }

      // Show success state with countdown
      setResetSuccess(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to reset password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Success state with countdown
  if (resetSuccess) {
    return (
      <div className="w-full space-y-8">
        <div className="flex flex-col items-start space-y-6">
          <Logo size="lg" />
          <div className="text-left space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-7 w-7 text-green-500" />
              <h1 className="text-2xl font-realhead tracking-tight text-white">
                Password reset successful
              </h1>
            </div>
            <p className="text-gray-400">
              Your password has been updated successfully.
            </p>
            <p className="text-gray-500 text-sm">
              Redirecting to sign in page in {countdown} second{countdown !== 1 ? 's' : ''}...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-8">
      {/* Logo and title - left aligned like LoginForm */}
      <div className="flex flex-col items-start space-y-6">
        <Logo size="lg" />
        <div className="text-left space-y-2">
          <h1 className="text-2xl font-realhead tracking-tight text-white">Create new password</h1>
          <p className="text-gray-400">Enter your new password below</p>
        </div>
      </div>

      {/* Form without card wrapper */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="password" className="text-gray-300">New password</Label>
          <Input
            id="password"
            type="password"
            placeholder="Enter your new password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoComplete="new-password"
            autoFocus
            className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
          />
          {passwordError && (
            <p className="text-sm text-destructive">{passwordError}</p>
          )}
          
          {/* Password strength indicator */}
          {password.length > 0 && (
            <div className="space-y-3 mt-3">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${
                      passwordStrength.strength === 'weak'
                        ? 'w-1/3 bg-red-500'
                        : passwordStrength.strength === 'medium'
                        ? 'w-2/3 bg-yellow-500'
                        : 'w-full bg-green-500'
                    }`}
                  />
                </div>
                <span className={`text-xs font-medium ${
                  passwordStrength.strength === 'weak'
                    ? 'text-red-500'
                    : passwordStrength.strength === 'medium'
                    ? 'text-yellow-500'
                    : 'text-green-500'
                }`}>
                  {passwordStrength.strength === 'weak' ? 'Weak' : 
                   passwordStrength.strength === 'medium' ? 'Medium' : 'Strong'}
                </span>
              </div>
              
              <div className="grid gap-1 text-xs">
                <RequirementItem met={passwordStrength.checks.minLength} text="At least 8 characters" />
                <RequirementItem met={passwordStrength.checks.hasUppercase} text="One uppercase letter" />
                <RequirementItem met={passwordStrength.checks.hasLowercase} text="One lowercase letter" />
                <RequirementItem met={passwordStrength.checks.hasNumber} text="One number" />
              </div>
            </div>
          )}
          
          {password.length === 0 && (
            <p className="text-xs text-gray-500">
              8+ characters, uppercase, lowercase, and number required
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-gray-300">Confirm password</Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="Confirm your new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            autoComplete="new-password"
            className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
          />
          {confirmError && (
            <p className="text-sm text-destructive">{confirmError}</p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Updating password...
            </>
          ) : (
            "Reset password"
          )}
        </Button>

        <div className="text-center">
          <button
            type="button"
            onClick={onBack}
            className="text-primary hover:underline text-sm"
          >
            Back to sign in
          </button>
        </div>
      </form>
    </div>
  );
}
